import React, { useEffect, useRef, useState } from "react";
import { StepAccordionStream } from "./streamSteps";
import CommonPopper from "../commonPopper";
import { Box, Button, Card, LinearProgress, Typography } from "@mui/material";
import ChatBubble from "../chatBubble";
import 'highlight.js/styles/github.css';

type StreamChunk = {
  step: number;
  step_title: string;
  chunk_id?: number;
  chunk_text?: string | object;
};

const SUGGESTED_QUESTIONS = ["Suggest a code fix"];

export const AIAvatar = () => (
  <Box
    sx={{
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: "linear-gradient(45deg, #00aefd, #2575fc)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      boxShadow: "0px 0px 10px #00aefd",
      color: "white",
      fontSize: 12,
      fontWeight: "bold",
    }}
  >
    AI
  </Box>
);

export const CodeFixStreamChat = () => {
  const [chunks, setChunks] = useState<StreamChunk[]>([]);
  const token = localStorage.getItem("token");
  const [fetchingResponse, setFetchingResponse] = useState<boolean>(false);
  const [currentStepTitle, setCurrentStepTitle] = useState<string>("");
  const chatBoxRef = useRef<HTMLDivElement | null>(null);

  const fetchStream = async (controller: AbortController, requestBody: any) => {
    setFetchingResponse(true);
    try {
      const response = await fetch("https://dev.aptori.dev/api/v1/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      if (!reader) throw new Error("No reader found on response body");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf("}{")) !== -1) {
          const jsonChunk = buffer.slice(0, idx + 1);
          buffer = buffer.slice(idx + 1);

          try {
            const parsed = JSON.parse(jsonChunk);
            setChunks((prev) => [...prev, parsed]);
            setCurrentStepTitle(parsed.step_title || ""); // Update current step title
          } catch (err) {
            console.warn("Failed to parse chunk:", jsonChunk);
          }
        }
      }

      // Final chunk
      if (buffer.trim()) {
        const lastParsed = JSON.parse(buffer);
        setChunks((prev) => [...prev, lastParsed]);
        setCurrentStepTitle(""); // Reset on final chunk
      }
    } catch (err) {
      console.error("Streaming error:", err);
    } finally {
      setFetchingResponse(false);
    }
  };

  const handleChatSubmit = () => {
    const controller = new AbortController();
    const requestBody = {
      github_url: "https://github.com/amsmu/python-test-repo",
      branch: "main",
      relative_file: "api.py",
      meta: {
        evidence:
          'user = get_user_by_username(data["username"])\nif user and user[1] == hash_password(data["password"]):',
        lines: "9-16",
        issues: [
          {
            rule: "Insecure Hash Function",
            description: "Use of weak hash function from external module",
            severity: "HIGH",
          },
        ],
      },
    };

    fetchStream(controller, requestBody);
    return () => controller.abort();
  };

  return (
    <CommonPopper
      elevatePopper
      disableHideOnClick
      trigger={
        <Box
          className="hand"
          sx={{
            position: "fixed",
            right: "18em",
            bottom: "3em",
            zIndex: 99,
            px: 3,
            py: 2,
            borderRadius: "12px",
            backgroundColor: "white",
            color: "black",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <AIAvatar />
            <Typography variant="h6">Code Fix</Typography>
          </Box>
        </Box>
      }
      child={
        <Card elevation={12} sx={{ p: 2, color: "white", borderRadius: 2, width: "40em" }}>
          <Box
            ref={chatBoxRef}
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              minHeight: "20em",
              width: "100%",
            }}
          >
            <ChatBubble position="left">
              <Typography variant="body2" sx={{ mt: 1 }}>
                Hi! I'm here to help you with your issue. You can ask me questions or provide feedback.
              </Typography>
            </ChatBubble>

            <Box sx={{ mt: 2, textAlign: "right" }}>
              {SUGGESTED_QUESTIONS.map((question, index) => (
                <Box key={index} sx={{ pb: 0.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ color: "text.primary", borderColor: "neutral.300" }}
                    onClick={handleChatSubmit}
                    disabled={fetchingResponse}
                  >
                    {question}
                  </Button>
                </Box>
              ))}
            </Box>

            {chunks.length > 0 && (
              <ChatBubble position="left">
                <StepAccordionStream chunks={chunks} currentStepTitle={currentStepTitle}/>
              </ChatBubble>
            )}
          </Box>

          {fetchingResponse && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                {currentStepTitle || "Thinking"}...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </Card>
      }
    />
  );
};